import React, { useEffect, useMemo, useState } from 'react';
import { Button, SimpleGrid, Stack, useToast } from '@chakra-ui/react';
import { ArrowLeft, Save } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCatalogue, getCatalogueItems } from '../../hooks/catalogueItems';
import {
  addOrderItem,
  deleteOrderItem,
  getOrderById,
  updateOrder,
  updateOrderItem,
} from '../../hooks/orders';
import { Lead, Order, OrderItem } from '../../utils/types';
import {
  OrderLineItemsSection,
  OrderMetaSection,
  OrderRecipientSection,
  OrderSummaryRail,
  createDraftOrderItem,
} from './components/orderBuilderShared';
import {
  OrderHero,
  OrderPrimaryButton,
  OrderSectionCard,
  OrderShell,
} from './components/orderUi';

const normalizeProducts = (data: unknown) => {
  if (Array.isArray(data)) {
    return data as Array<{ id: string; name: string; price?: number }>;
  }

  if (data && typeof data === 'object' && Array.isArray((data as { results?: unknown[] }).results)) {
    return (data as { results: Array<{ id: string; name: string; price?: number }> }).results;
  }

  return [];
};

type OrderFormState = {
  source: string;
  status: string;
  payment_status: string;
  fulfillment_status: string;
  priority: string;
  notes: string;
  internal_notes: string;
  delivery_method: string;
  delivery_address: string;
  delivery_notes: string;
  requested_delivery_date: string;
};

const createFormState = (order: Order): OrderFormState => ({
  source: order.source || 'wa',
  status: order.status || 'draft',
  payment_status: order.payment_status || 'unpaid',
  fulfillment_status: order.fulfillment_status || 'unfulfilled',
  priority: order.priority || 'normal',
  notes: order.notes || '',
  internal_notes: order.internal_notes || '',
  delivery_method: order.delivery_method || 'pickup',
  delivery_address: order.delivery_address || '',
  delivery_notes: order.delivery_notes || '',
  requested_delivery_date: order.requested_delivery_date
    ? String(order.requested_delivery_date).slice(0, 10)
    : '',
});

const normalizeOrderItems = (items?: OrderItem[]) =>
  (items || []).map((item) => ({
    ...item,
    quantity: Number(item.quantity || 1),
    unit_price: Number(item.unit_price ?? item.price ?? 0),
    price: Number(item.unit_price ?? item.price ?? 0),
    total_price:
      Number(item.total_price ?? 0) ||
      Number(item.quantity || 0) * Number(item.unit_price ?? item.price ?? 0),
  }));

export default function EditOrderPage() {
  const { orgId, orderId, catalogueId } = useParams<{
    orgId: string;
    orderId: string;
    catalogueId?: string;
  }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [order, setOrder] = useState<Order | null>(null);
  const [products, setProducts] = useState<Array<{ id: string; name: string; price?: number }>>([]);
  const [items, setItems] = useState<OrderItem[]>([]);
  const [removedItemIds, setRemovedItemIds] = useState<Array<string | number>>([]);
  const [form, setForm] = useState<OrderFormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      const orderResponsePromise = getOrderById(orgId, orderId);
      const resolvedCatalogueId = catalogueId
        ? catalogueId
        : ((await getCatalogue(orgId || ''))?.data?.id as string | undefined);
      const productResponsePromise = resolvedCatalogueId
        ? getCatalogueItems(orgId || '', String(resolvedCatalogueId))
        : Promise.resolve({ success: true, data: [] });

      const [orderResponse, productResponse] = await Promise.all([
        orderResponsePromise,
        productResponsePromise,
      ]);

      if (!mounted) {
        return;
      }

      if (orderResponse?.success && orderResponse.data && typeof orderResponse.data === 'object') {
        const nextOrder = orderResponse.data as Order;
        setOrder(nextOrder);
        setForm(createFormState(nextOrder));
        setItems(normalizeOrderItems(nextOrder.items));
      } else {
        toast({
          title: 'Could not load order',
          description: 'Please refresh the page and try again.',
          status: 'error',
          duration: 3500,
          isClosable: true,
        });
      }

      if (productResponse?.success) {
        setProducts(normalizeProducts(productResponse.data));
      }

      setLoading(false);
    };

    load();
    return () => {
      mounted = false;
    };
  }, [catalogueId, orderId, orgId, toast]);

  const selectedLead = useMemo<Lead | null>(() => order?.lead || null, [order]);

  const handleChangeForm = (field: string, value: string) => {
    setForm((current) => (current ? { ...current, [field]: value } : current));
  };

  const handleAddItem = () => {
    setItems((current) => [...current, createDraftOrderItem()]);
  };

  const handleDuplicateItem = (index: number) => {
    setItems((current) => {
      const item = current[index];
      if (!item) {
        return current;
      }

      const duplicate = {
        ...item,
        ...createDraftOrderItem(),
        product_name: item.product_name,
        description: item.description,
        sku: item.sku,
        quantity: item.quantity,
        unit_price: Number(item.unit_price ?? item.price ?? 0),
        total_price:
          Number(item.quantity || 0) * Number(item.unit_price ?? item.price ?? 0),
      };

      const next = [...current];
      next.splice(index + 1, 0, duplicate);
      return next;
    });
  };

  const handleChangeItem = (
    index: number,
    field: keyof OrderItem,
    value: string | number,
  ) => {
    setItems((current) => {
      const next = [...current];
      const item = next[index];
      if (!item) {
        return current;
      }

      const updated: OrderItem = { ...item, [field]: value } as OrderItem;

      if (field === 'product_name') {
        const selectedProduct = products.find((product) => product.name === value);
        updated.unit_price = Number(selectedProduct?.price || updated.unit_price || 0);
      }

      updated.total_price =
        Number(updated.quantity || 0) * Number(updated.unit_price ?? updated.price ?? 0);
      updated.price = Number(updated.unit_price ?? updated.price ?? 0);
      next[index] = updated;
      return next;
    });
  };

  const handleRemoveItem = (index: number) => {
    setItems((current) => {
      const item = current[index];
      if (item?.id && !String(item.id).startsWith('draft-')) {
        setRemovedItemIds((existing) => [...existing, item.id as string | number]);
      }
      return current.filter((_, itemIndex) => itemIndex !== index);
    });
  };

  const handleSave = async () => {
    if (!orgId || !orderId || !form) {
      return;
    }

    const validItems = items.filter((item) => item.product_name.trim());
    if (validItems.length === 0) {
      toast({
        title: 'Add at least one line item',
        description: 'Add at least one product or service line before saving your changes.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);
    try {
      const orderResponse = await updateOrder(orgId, orderId, {
        source: form.source,
        status: form.status,
        payment_status: form.payment_status,
        fulfillment_status: form.fulfillment_status,
        priority: form.priority,
        notes: form.notes,
        internal_notes: form.internal_notes,
        delivery_method: form.delivery_method,
        delivery_address: form.delivery_address,
        delivery_notes: form.delivery_notes,
        requested_delivery_date: form.requested_delivery_date || null,
      });

      if (!orderResponse.success) {
        throw new Error('Order update failed');
      }

      for (const itemId of removedItemIds) {
        const deleteResponse = await deleteOrderItem(orgId, orderId, itemId);
        if (!deleteResponse.success) {
          throw new Error('Item delete failed');
        }
      }

      for (const item of validItems) {
        const payload = {
          product_name: item.product_name,
          description: item.description || '',
          sku: item.sku || '',
          quantity: Number(item.quantity || 1),
          unit_price: Number(item.unit_price ?? item.price ?? 0),
        };

        if (item.id && !String(item.id).startsWith('draft-')) {
          const updateResponse = await updateOrderItem(orgId, orderId, item.id, payload);
          if (!updateResponse.success) {
            throw new Error('Item update failed');
          }
        } else {
          const createResponse = await addOrderItem(orgId, orderId, payload);
          if (!createResponse.success) {
            throw new Error('Item creation failed');
          }
        }
      }

      toast({
        title: 'Order saved',
        description: 'Your changes have been saved and the order is up to date.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      navigate(`/organization/${orgId}/orders/${orderId}`);
    } catch (error) {
      toast({
        title: 'Could not save order',
        description: 'Please review your changes and try again.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !order || !form) {
    return (
      <OrderShell compact>
        <OrderSectionCard title="Loading order" description="Please wait while the order is loading.">
          <Button variant="ghost" isLoading loadingText="Loading" />
        </OrderSectionCard>
      </OrderShell>
    );
  }

  return (
    <OrderShell compact>
      <OrderHero
        eyebrow="Edit order"
        title={order.reference || 'Order editor'}
        description="Update items, delivery details, and notes without leaving the order workspace."
        primaryAction={
          <OrderPrimaryButton
            leftIcon={<Save size={16} />}
            onClick={handleSave}
            isLoading={saving}
            loadingText="Saving"
          >
            Save changes
          </OrderPrimaryButton>
        }
        secondaryAction={
          <Button borderRadius="16px" variant="outline" leftIcon={<ArrowLeft size={16} />} onClick={() => navigate(-1)}>
            Back
          </Button>
        }
        stats={[
          { label: 'Status', value: order.status?.toUpperCase() || 'DRAFT' },
          { label: 'Payment', value: order.payment_status?.replace(/_/g, ' ') || 'UNPAID' },
          { label: 'Created', value: order.created_at ? order.created_at.slice(0, 10) : 'Unknown' },
          { label: 'Updated', value: order.updated_at ? order.updated_at.slice(0, 10) : 'Unknown' },
        ]}
      />

      <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6} alignItems="start">
        <Stack spacing={6} gridColumn={{ xl: 'span 2' }}>
          <OrderRecipientSection
            selectedLeadId={String(order.lead?.id || '')}
            selectedLead={selectedLead}
            selector={<Button variant="outline" borderRadius="16px" isDisabled>Customer comes from the linked lead</Button>}
          />

          <OrderLineItemsSection
            items={items}
            products={products}
            onAddItem={handleAddItem}
            onDuplicateItem={handleDuplicateItem}
            onChangeItem={handleChangeItem}
            onRemoveItem={handleRemoveItem}
          />

          <OrderMetaSection
            source={form.source}
            status={form.status}
            paymentStatus={form.payment_status}
            fulfillmentStatus={form.fulfillment_status}
            priority={form.priority}
            requestedDeliveryDate={form.requested_delivery_date}
            deliveryMethod={form.delivery_method}
            notes={form.notes}
            internalNotes={form.internal_notes}
            deliveryAddress={form.delivery_address}
            deliveryNotes={form.delivery_notes}
            onChange={handleChangeForm}
          />
        </Stack>

        <Stack spacing={6}>
          <OrderSummaryRail
            reference={order.reference}
            status={form.status}
            paymentStatus={form.payment_status}
            fulfillmentStatus={form.fulfillment_status}
            items={items}
            requestedDeliveryDate={form.requested_delivery_date}
            source={form.source}
            priority={form.priority}
          />
          <OrderSectionCard
            title="Save state"
            description="Save your changes once the order details feel right."
          >
            <Stack spacing={3}>
              <OrderPrimaryButton
                leftIcon={<Save size={16} />}
                onClick={handleSave}
                isLoading={saving}
                loadingText="Saving"
              >
                Save changes
              </OrderPrimaryButton>
              <Button borderRadius="16px" variant="outline" onClick={() => navigate(`/organization/${orgId}/orders/${orderId}`)}>
                Back to order
              </Button>
            </Stack>
          </OrderSectionCard>
        </Stack>
      </SimpleGrid>
    </OrderShell>
  );
}
